// "Purchase Approvals" - the Purchase Approvals page of the Purchase page
// (PurchasePanel with productApprovalMode="admin" or "pm"). Summary cards over
// the purchase requests, filters, the New Product Approvals table and a
// details panel with Approve / Reject.
//   viewer="admin" Same rules as ProductApprovalsView mode="admin": the admin
//                  sees every approval, the purchase manager's approval /
//                  rejection is marked on each row, and approving anything the
//                  purchase manager did not approve opens the review form
//                  (NewProductApprovalModal) so the admin fills max price + vendor.
//   viewer="pm"    The purchase manager: same page, but only what is waiting
//                  for the purchase manager can be acted on. Accept opens the
//                  review form for max price + vendor (then it goes to the
//                  admin); Reject works from the panel.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Briefcase,
    LayoutList,
    FileText,
    Search,
    Eye,
    X,
    Package,
    Paperclip,
    ChevronLeft,
    ChevronRight,
    BadgeCheck,
    XCircle,
    Clock,
    Check,
} from 'lucide-react';
import {
    getProductApprovals,
    approveProductApproval,
    rejectProductApproval,
    getProductApprovalAttachment,
} from '../../../api';
import showToast from '../../../utils/toast';
import { confirmDialog } from '../../../utils/dialogs';
import NewProductApprovalModal from './NewProductApprovalModal';
import {
    pmDecisionOf,
    isRejectedByPm,
    isAdminActionable,
    isPendingApproval as isPending,
    isPendingPmApproval as isPendingPm,
} from './productApprovalReview';
import PurchaseSummaryCards, { CARD_STATUS, formatRupees } from './PurchaseSummaryCards';
import './AdminPurchaseApprovals.css';
import SearchableSelect from '../../common/SearchableSelect';

const ITEMS_PER_PAGE = 10;

const CATEGORY_OPTIONS = ['ISF Shop', 'Medicines', 'Consumables', 'Repairs', 'Infra', 'Others'];

const PRIORITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

const APPROVED_TAB = { key: 'approved', label: 'Approved', title: 'Approved', match: (item) => item.status === 'approved' };
const REJECTED_TAB = { key: 'rejected', label: 'Rejected', title: 'Rejected', match: (item) => item.status === 'rejected' };
const ALL_TAB = { key: 'all', label: 'All', title: 'All', match: () => true };

// Which approvals the table lists. Admin: "Pending" is waiting on the
// purchase manager or the admin, the same count ProductApprovalsView shows the
// admin. Purchase manager: "Pending" is what waits for them, and the ones they
// accepted have their own tab while they wait for the admin.
const LIST_TABS = {
    admin: [
        { key: 'pending', label: 'Pending', title: 'Pending Approval', match: isPending },
        APPROVED_TAB,
        REJECTED_TAB,
        ALL_TAB,
    ],
    pm: [
        { key: 'pending', label: 'Pending', title: 'Pending Approval', match: isPendingPm },
        { key: 'sent', label: 'Sent to Admin', title: 'Sent to Admin', match: (item) => item.status === 'pending_admin' },
        APPROVED_TAB,
        REJECTED_TAB,
        ALL_TAB,
    ],
};

// What each viewer may do with one approval (the backend checks the same).
const ACCESS = {
    admin: {
        canApprove: isAdminActionable,
        canReject: isPending,
        // Without the purchase manager's approval there is no max price / vendor yet.
        needsPricing: (item) => item.status !== 'pending_admin',
        reviewMode: 'admin-review',
    },
    pm: {
        canApprove: isPendingPm,
        canReject: isPendingPm,
        // The purchase manager always adds max price + vendor when accepting.
        needsPricing: () => true,
        reviewMode: 'pm-review',
    },
};

// Purchase manager's call, keyed by pmDecisionOf() ('none' = not reviewed).
// short: screen-reader text of the table icon; chip: the details panel chip.
const PM_REVIEW = {
    approved: { label: 'Approved by Purchase Manager', short: 'PM approved', chip: 'Approved', tone: 'green', Icon: BadgeCheck },
    rejected: { label: 'Rejected by Purchase Manager', short: 'PM rejected', chip: 'Rejected', tone: 'red', Icon: XCircle },
    none: { label: 'Not reviewed by Purchase Manager', short: '', chip: 'Not reviewed', tone: 'grey', Icon: Clock },
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('en-IN');
};

const monthOf = (date) => {
    const parsed = date ? new Date(date) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return '';
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

const formatRole = (role) =>
    (role || '')
        .toString()
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const requesterIdOf = (item) => String(item.requestedBy?._id || item.requestedBy || '');

// "Asmita (Coach)"
const requesterLabel = (item) => {
    const name = item.requestedBy?.name || item.requestedBy?.email || 'N/A';
    const role = formatRole(item.requestedByRole || item.requestedBy?.role);
    return role ? `${name} (${role})` : name;
};

const balagruhaIdOf = (item) => String(item.balagruhaId?._id || item.balagruhaId || '');

// The maximum price is per unit (see NewProductApprovalModal).
const estimatedCostOf = (item) => (item.maxPrice ? Number(item.maxPrice) * Number(item.quantity || 0) : null);

const statusText = (item) => {
    if (item.status === 'pending_pm') return 'Waiting for Purchase Manager';
    if (item.status === 'pending_admin') return 'Waiting for Admin';
    if (item.status === 'rejected') {
        return item.rejectedStage === 'admin' ? 'Rejected by Admin' : 'Rejected by Purchase Manager';
    }
    if (item.status === 'approved') {
        return pmDecisionOf(item) === 'approved' ? 'Approved' : 'Approved by Admin (direct)';
    }
    return item.status || 'N/A';
};

const isImageFile = (attachment) => /\.(jpe?g|png|gif|webp)$/i.test(attachment?.filename || '');

const openAttachment = async (approvalId, attachment) => {
    try {
        const blob = await getProductApprovalAttachment(approvalId, attachment._id);
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
        showToast('Could not open attachment', 'error');
    }
};

const errorMessage = (error) => error?.response?.data?.message || error?.message || 'Something went wrong';

function PriorityBadge({ priority }) {
    const key = PRIORITY_LABEL[priority] ? priority : 'medium';
    return <span className={`apa-priority apa-priority-${key}`}>{PRIORITY_LABEL[key]}</span>;
}

// Image attachments of one approval as object URLs (attachment id -> url),
// fetched through the authenticated attachment endpoint.
function useImagePreviews(approvalId, attachments) {
    const [previews, setPreviews] = useState({});
    const imageIds = attachments.filter(isImageFile).map((attachment) => attachment._id).join(',');

    useEffect(() => {
        setPreviews({});
        if (!approvalId || !imageIds || typeof URL.createObjectURL !== 'function') return undefined;
        let cancelled = false;
        const urls = [];
        imageIds.split(',').forEach(async (attachmentId) => {
            try {
                const blob = await getProductApprovalAttachment(approvalId, attachmentId);
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                urls.push(url);
                setPreviews((prev) => ({ ...prev, [attachmentId]: url }));
            } catch (error) {
                // No thumbnail; the attachment still opens from its file chip.
            }
        });
        return () => {
            cancelled = true;
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [approvalId, imageIds]);

    return previews;
}

const VISIBLE_ATTACHMENTS = 3;

// Final decision line once the viewer can no longer act on it. The admin sees
// its own decision; the purchase manager also sees its own (sent to the admin
// or rejected) until the admin decides.
const decisionText = (approval, isPm) => {
    const byPm = isPm && (approval.status === 'pending_admin' || isRejectedByPm(approval));
    const reviewer = byPm ? approval.pmReviewedBy : approval.adminReviewedBy;
    const reviewedAt = byPm ? approval.pmReviewedAt : approval.adminReviewedAt;
    return [
        isPm && approval.status === 'pending_admin' ? 'Sent to Admin' : statusText(approval),
        reviewer?.name ? `by ${reviewer.name}` : '',
        reviewedAt ? `on ${formatDate(reviewedAt)}` : '',
    ]
        .filter(Boolean)
        .join(' ');
};

function ApprovalDetails({ approval, viewer = 'admin', busy, onClose, onApprove, onReject, onOpenForm }) {
    const [remarks, setRemarks] = useState('');
    const [showAllAttachments, setShowAllAttachments] = useState(false);
    const attachments = approval.attachments || [];
    const previews = useImagePreviews(approval._id, attachments);
    const cover = attachments.find((attachment) => previews[attachment._id]);
    const visibleAttachments = showAllAttachments ? attachments : attachments.slice(0, VISIBLE_ATTACHMENTS);
    const hiddenCount = attachments.length - visibleAttachments.length;

    const isPm = viewer === 'pm';
    const access = ACCESS[viewer] || ACCESS.admin;
    const pmReview = PM_REVIEW[pmDecisionOf(approval) || 'none'];
    const actionable = access.canApprove(approval);
    const needsPricing = actionable && access.needsPricing(approval);
    const canReject = access.canReject(approval);
    const cost = estimatedCostOf(approval);
    const vendor = approval.vendor || {};
    const hasVendor = Boolean(vendor.name || vendor.address || vendor.phone);
    const approveLabel = isPm ? 'Accept' : 'Approve';

    const rows = [
        ['Requested By', requesterLabel(approval)],
        ['Request Date', formatDate(approval.createdAt)],
        ['Due Date', formatDate(approval.deadline)],
        ['Balgruha', approval.balagruhaId?.name || 'N/A'],
        ['Category', approval.category || 'N/A'],
        ['Quantity', approval.quantity ?? 'N/A'],
        [
            'Estimated Cost',
            cost !== null
                ? `₹ ${formatRupees(cost)} (₹ ${formatRupees(approval.maxPrice)} per unit)`
                : 'Not set yet',
        ],
        ['Priority', <PriorityBadge priority={approval.priority} />],
        ['Status', statusText(approval)],
        [
            'Purchase Manager',
            <span className={`apa-pm-chip apa-pm-${pmReview.tone}`} title={pmReview.label}>
                <pmReview.Icon size={13} aria-hidden="true" /> {pmReview.chip}
            </span>,
        ],
    ];

    return (
        <aside className="apa-details" aria-label="Product approval details">
            <div className="apa-details-head">
                <span className="apa-details-icon"><FileText size={16} aria-hidden="true" /></span>
                <h3>Product Approval Details</h3>
                <button type="button" className="apa-icon-btn" onClick={onClose} aria-label="Close details">
                    <X size={18} />
                </button>
            </div>

            <div className="apa-details-body">
                <div className="apa-product">
                    <div className="apa-product-image">
                        {cover ? (
                            <img src={previews[cover._id]} alt={approval.productName} />
                        ) : (
                            <Package size={30} aria-hidden="true" />
                        )}
                    </div>
                    <div className="apa-product-text">
                        <div className="apa-product-title">
                            <strong>{approval.productName}</strong>
                            <span className={`apa-priority-tag apa-priority-${PRIORITY_LABEL[approval.priority] ? approval.priority : 'medium'}`}>
                                {PRIORITY_LABEL[approval.priority] || 'Medium'} Priority
                            </span>
                        </div>
                        {approval.description && <p>{approval.description}</p>}
                        {approval.approvalId && <small>{approval.approvalId}</small>}
                    </div>
                </div>

                <section className="apa-section">
                    <h4>Request Details</h4>
                    <dl className="apa-dl">
                        {rows.map(([label, value]) => (
                            <React.Fragment key={label}>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </section>

                <section className="apa-section">
                    <h4>Vendor Details</h4>
                    {hasVendor ? (
                        <dl className="apa-dl">
                            <dt>Vendor / Shop Name</dt>
                            <dd>{vendor.name || '-'}</dd>
                            <dt>Address</dt>
                            <dd>{vendor.address || '-'}</dd>
                            {vendor.phone && (
                                <>
                                    <dt>Phone</dt>
                                    <dd>{vendor.phone}</dd>
                                </>
                            )}
                            {vendor.contactPerson && (
                                <>
                                    <dt>Contact Person</dt>
                                    <dd>{vendor.contactPerson}</dd>
                                </>
                            )}
                        </dl>
                    ) : (
                        <p className="apa-muted">
                            {needsPricing
                                ? `Not added yet. You add the vendor and maximum price when you ${isPm ? 'accept' : 'approve'}.`
                                : 'No vendor details.'}
                        </p>
                    )}
                </section>

                <section className="apa-section">
                    <h4>Attachments</h4>
                    {attachments.length === 0 ? (
                        <p className="apa-muted">No attachments</p>
                    ) : (
                        <div className="apa-attachments">
                            {visibleAttachments.map((attachment) => (
                                <button
                                    type="button"
                                    key={attachment._id}
                                    className={previews[attachment._id] ? 'apa-thumb' : 'apa-file-chip'}
                                    title={attachment.filename}
                                    onClick={() => openAttachment(approval._id, attachment)}
                                >
                                    {previews[attachment._id] ? (
                                        <img src={previews[attachment._id]} alt={attachment.filename} />
                                    ) : (
                                        <>
                                            <Paperclip size={12} aria-hidden="true" />
                                            <span>{attachment.filename}</span>
                                        </>
                                    )}
                                </button>
                            ))}
                            {hiddenCount > 0 && (
                                <button type="button" className="apa-more-chip" onClick={() => setShowAllAttachments(true)}>
                                    +{hiddenCount} more
                                </button>
                            )}
                        </div>
                    )}
                </section>

                {(approval.pmNotes || approval.adminNotes) && (
                    <section className="apa-section">
                        {approval.pmNotes && (
                            <div className="apa-note">
                                <h4>Note from Purchase Manager</h4>
                                <p>{approval.pmNotes}</p>
                            </div>
                        )}
                        {approval.adminNotes && (
                            <div className="apa-note">
                                <h4>Note from Admin</h4>
                                <p>{approval.adminNotes}</p>
                            </div>
                        )}
                    </section>
                )}

                {/* Sent with Reject, and with Approve when no review form is needed */}
                {canReject && (
                    <label className="apa-remarks">
                        <span>Remarks (Optional)</span>
                        <textarea
                            rows={2}
                            maxLength={500}
                            value={remarks}
                            placeholder={needsPricing ? 'Reason for rejection' : 'Reason for rejection or a note on the approval'}
                            onChange={(e) => setRemarks(e.target.value)}
                        />
                    </label>
                )}
            </div>

            <div className="apa-details-actions">
                {actionable ? (
                    <>
                        {needsPricing && (
                            <p className="apa-muted apa-action-hint">
                                {isPm
                                    ? 'Accepting opens the review form to add the maximum price and vendor, then it goes to the Admin.'
                                    : 'Approving opens the review form to add the maximum price and vendor.'}
                            </p>
                        )}
                        <div className="apa-action-buttons">
                            {canReject && (
                                <button
                                    type="button"
                                    className="apa-btn apa-btn-reject"
                                    disabled={busy}
                                    onClick={() => onReject(approval, remarks.trim())}
                                >
                                    <X size={16} aria-hidden="true" /> Reject
                                </button>
                            )}
                            <button
                                type="button"
                                className="apa-btn apa-btn-approve"
                                disabled={busy}
                                onClick={() => onApprove(approval, remarks.trim())}
                            >
                                <Check size={16} aria-hidden="true" /> {approveLabel}
                            </button>
                        </div>
                    </>
                ) : (
                    <p className={`apa-final apa-final-${approval.status}`}>{decisionText(approval, isPm)}</p>
                )}
                <button type="button" className="apa-link-btn" onClick={() => onOpenForm(approval)}>
                    Open full form
                </button>
            </div>
        </aside>
    );
}

export default function AdminPurchaseApprovalsView({
    balagruhas = [],
    purchaseData = [],
    onShowRequests = () => {},
    // Called with the approvals after every load (the Purchase Approvals
    // button counts what waits for this viewer).
    onApprovalsLoaded = () => {},
    viewer = 'admin',
}) {
    const isPm = viewer === 'pm';
    const access = ACCESS[viewer] || ACCESS.admin;
    const listTabs = LIST_TABS[viewer] || LIST_TABS.admin;
    const matchesPending = listTabs[0].match;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('pending');
    const [balagruhaFilter, setBalagruhaFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [requesterFilter, setRequesterFilter] = useState('all');
    const [monthFilter, setMonthFilter] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [checkedIds, setCheckedIds] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    // Approval open in the full review form (NewProductApprovalModal)
    const [formApproval, setFormApproval] = useState(null);
    // Purchase manager's Accept from the panel opens the form already accepted.
    const [formAccepted, setFormAccepted] = useState(false);
    const [busy, setBusy] = useState(false);
    const autoSelected = useRef(false);
    const onApprovalsLoadedRef = useRef(onApprovalsLoaded);
    onApprovalsLoadedRef.current = onApprovalsLoaded;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getProductApprovals();
            const loaded = response?.success ? response.data || [] : [];
            setItems(loaded);
            onApprovalsLoadedRef.current(loaded);
        } catch (error) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Open the first pending product the first time the list loads.
    useEffect(() => {
        if (autoSelected.current || items.length === 0) return;
        autoSelected.current = true;
        const first = items.find(matchesPending);
        if (first) setSelectedId(first._id);
    }, [items, matchesPending]);

    const resetPage = () => {
        setPage(1);
        setCheckedIds([]);
    };

    const balagruhaOptions = useMemo(() => {
        const names = new Map();
        balagruhas.forEach((bg) => names.set(String(bg._id), bg.name));
        items.forEach((item) => {
            if (item.balagruhaId?.name) names.set(balagruhaIdOf(item), item.balagruhaId.name);
        });
        return [...names.entries()]
            .map(([id, name]) => ({ id, name: name || 'N/A' }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [balagruhas, items]);

    const requesterOptions = useMemo(() => {
        const labels = new Map();
        items.forEach((item) => {
            const id = requesterIdOf(item);
            if (id) labels.set(id, requesterLabel(item));
        });
        return [...labels.entries()]
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [items]);

    const tabCounts = useMemo(
        () => Object.fromEntries(listTabs.map((t) => [t.key, items.filter(t.match).length])),
        [items, listTabs]
    );

    const filtered = useMemo(() => {
        const matchesTab = listTabs.find((t) => t.key === tab)?.match || matchesPending;
        const query = search.trim().toLowerCase();
        return items.filter((item) => {
            if (!matchesTab(item)) return false;
            if (balagruhaFilter !== 'all' && balagruhaIdOf(item) !== balagruhaFilter) return false;
            if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
            if (requesterFilter !== 'all' && requesterIdOf(item) !== requesterFilter) return false;
            if (monthFilter && monthOf(item.createdAt) !== monthFilter) return false;
            if (query) {
                const haystack = [
                    item.approvalId,
                    item.productName,
                    requesterLabel(item),
                    item.balagruhaId?.name,
                    item.description,
                    item.pmNotes,
                    item.adminNotes,
                    item.vendor?.name,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!haystack.includes(query)) return false;
            }
            return true;
        });
    }, [items, listTabs, matchesPending, tab, balagruhaFilter, categoryFilter, requesterFilter, monthFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
    const firstPageButton = Math.max(1, safePage - 2);
    const pageButtons = [];
    for (let num = firstPageButton; num <= Math.min(totalPages, firstPageButton + 4); num += 1) {
        pageButtons.push(num);
    }

    const pageIds = pageItems.map((item) => item._id);
    const allPageChecked = pageIds.length > 0 && pageIds.every((id) => checkedIds.includes(id));
    const checkedItems = filtered.filter((item) => checkedIds.includes(item._id));
    // Bulk approve only covers what the purchase manager approved - the rest
    // need max price + vendor from the review form. The purchase manager adds
    // them to every product, so it can only bulk-reject.
    const bulkApprovable = isPm ? [] : checkedItems.filter((item) => item.status === 'pending_admin');
    const bulkRejectable = checkedItems.filter(access.canReject);

    const selected = items.find((item) => item._id === selectedId) || null;

    const showPendingApprovals = () => {
        setTab('pending');
        resetPage();
    };

    // Request cards open the request list filtered to their status.
    const handleCardSelect = (key) => {
        if (key === 'pendingApproval') showPendingApprovals();
        else onShowRequests(CARD_STATUS[key]);
    };

    const toggleChecked = (id) =>
        setCheckedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));

    const toggleAllOnPage = () =>
        setCheckedIds((prev) =>
            allPageChecked ? prev.filter((id) => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]
        );

    const run = async (action, successMessage) => {
        try {
            setBusy(true);
            const response = await action();
            if (!response?.success) throw new Error(response?.message);
            showToast(successMessage, 'success');
            await load();
        } catch (error) {
            showToast(errorMessage(error), 'error');
        } finally {
            setBusy(false);
        }
    };

    const openForm = (approval, accepted = false) => {
        setFormAccepted(accepted);
        setFormApproval(approval);
    };

    const handleApprove = (approval, remarks) => {
        // Not approved by the purchase manager: the admin fills max price +
        // vendor in the review form first. The purchase manager always does.
        if (access.needsPricing(approval)) {
            openForm(approval, isPm);
            return;
        }
        run(() => approveProductApproval(approval._id, { notes: remarks }), 'Product approved');
    };

    const handleReject = async (approval, remarks) => {
        const confirmed = await confirmDialog(`Reject "${approval.productName}"?`, {
            title: 'Reject product',
            confirmText: 'Reject',
            danger: true,
        });
        if (!confirmed) return;
        run(() => rejectProductApproval(approval._id, remarks), 'Product rejected');
    };

    const runBulk = async (list, action, verb) => {
        try {
            setBusy(true);
            const results = await Promise.allSettled(list.map(action));
            const done = results.filter((result) => result.status === 'fulfilled' && result.value?.success).length;
            const failed = list.length - done;
            if (done > 0) showToast(`${done} product${done === 1 ? '' : 's'} ${verb}`, 'success');
            if (failed > 0) showToast(`${failed} product${failed === 1 ? '' : 's'} could not be ${verb}`, 'error');
            setCheckedIds([]);
            await load();
        } finally {
            setBusy(false);
        }
    };

    const approveChecked = async () => {
        const skipped = checkedItems.length - bulkApprovable.length;
        const confirmed = await confirmDialog(
            `Approve ${bulkApprovable.length} product${bulkApprovable.length === 1 ? '' : 's'} the Purchase Manager already approved?` +
                (skipped > 0
                    ? `\n${skipped} other selected product${skipped === 1 ? ' needs' : 's need'} the maximum price and vendor - open ${skipped === 1 ? 'it' : 'them'} to approve.`
                    : ''),
            { title: 'Approve selected', confirmText: 'Approve' }
        );
        if (!confirmed) return;
        runBulk(bulkApprovable, (item) => approveProductApproval(item._id, {}), 'approved');
    };

    const rejectChecked = async () => {
        const confirmed = await confirmDialog(
            `Reject ${bulkRejectable.length} selected product${bulkRejectable.length === 1 ? '' : 's'}?`,
            { title: 'Reject selected', confirmText: 'Reject', danger: true }
        );
        if (!confirmed) return;
        runBulk(bulkRejectable, (item) => rejectProductApproval(item._id, ''), 'rejected');
    };

    const activeTab = listTabs.find((t) => t.key === tab) || listTabs[0];
    const showStatusColumn = tab !== 'pending';
    const columnCount = showStatusColumn ? 11 : 10;

    return (
        <div className="apa-root">
            <div className="apa-header">
                <div className="apa-heading">
                    <span className="apa-heading-icon"><Briefcase size={22} color="#ffffff" aria-hidden="true" /></span>
                    <div>
                        <h2>Purchase Approvals</h2>
                        <p>
                            {isPm
                                ? 'Review new product requests and send them to Admin for approval'
                                : 'Review and approve purchase requests from Purchase Manager'}
                        </p>
                    </div>
                </div>
                <button type="button" className="apa-outline-btn" onClick={() => onShowRequests('all')}>
                    <LayoutList size={16} aria-hidden="true" /> View All Purchases
                </button>
            </div>

            <PurchaseSummaryCards
                purchaseData={purchaseData}
                pendingApprovals={tabCounts.pending}
                approvalsHint={isPm ? 'Need your review' : 'Need admin review'}
                activeKey={tab === 'pending' ? 'pendingApproval' : null}
                onSelect={handleCardSelect}
            />

            <div className="apa-filters">
                <label className="apa-field">
                    <span>Balgruha Name</span>
                    <SearchableSelect searchPlaceholder="Search balagruha..." value={balagruhaFilter} onChange={(e) => { setBalagruhaFilter(e.target.value); resetPage(); }}>
                        <option value="all">All Balgruhas</option>
                        {balagruhaOptions.map((bg) => (
                            <option key={bg.id} value={bg.id}>{bg.name}</option>
                        ))}
                    </SearchableSelect>
                </label>
                <label className="apa-field">
                    <span>Category</span>
                    <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); resetPage(); }}>
                        <option value="all">All Categories</option>
                        {CATEGORY_OPTIONS.map((category) => (
                            <option key={category} value={category}>{category}</option>
                        ))}
                    </select>
                </label>
                <label className="apa-field">
                    <span>Requested By</span>
                    <SearchableSelect searchPlaceholder="Search requester..." value={requesterFilter} onChange={(e) => { setRequesterFilter(e.target.value); resetPage(); }}>
                        <option value="all">All Requesters</option>
                        {requesterOptions.map((requester) => (
                            <option key={requester.id} value={requester.id}>{requester.label}</option>
                        ))}
                    </SearchableSelect>
                </label>
                <label className="apa-field">
                    <span>Date Range</span>
                    <input
                        type="month"
                        value={monthFilter}
                        onChange={(e) => { setMonthFilter(e.target.value); resetPage(); }}
                    />
                </label>
                <div className="apa-search">
                    <Search size={16} className="apa-search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        aria-label="Search approvals"
                        placeholder="Search by item name, requester, notes..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                    />
                </div>
            </div>

            <div className={`apa-body${selected ? ' has-details' : ''}`}>
                <section className="apa-table-card">
                    <div className="apa-table-head">
                        <h3>{activeTab.title} <span>(New Product Approvals)</span></h3>
                        <div className="apa-tabs" role="tablist" aria-label="Approval status">
                            {listTabs.map((t) => (
                                <button
                                    type="button"
                                    role="tab"
                                    key={t.key}
                                    aria-selected={tab === t.key}
                                    className={tab === t.key ? 'active' : ''}
                                    onClick={() => { setTab(t.key); resetPage(); }}
                                >
                                    {t.label}
                                    {/* No count while it is 0 */}
                                    {tabCounts[t.key] > 0 && <span className="apa-tab-count">{tabCounts[t.key]}</span>}
                                </button>
                            ))}
                        </div>
                    </div>

                    {checkedItems.length > 0 && (
                        <div className="apa-bulk-bar">
                            <span>{checkedItems.length} selected</span>
                            {!isPm && (
                                <button
                                    type="button"
                                    className="apa-btn apa-btn-approve apa-btn-sm"
                                    disabled={busy || bulkApprovable.length === 0}
                                    title={bulkApprovable.length === 0 ? 'Only products the Purchase Manager approved can be approved together' : undefined}
                                    onClick={approveChecked}
                                >
                                    <Check size={14} aria-hidden="true" /> Approve
                                </button>
                            )}
                            <button
                                type="button"
                                className="apa-btn apa-btn-reject apa-btn-sm"
                                disabled={busy || bulkRejectable.length === 0}
                                onClick={rejectChecked}
                            >
                                <X size={14} aria-hidden="true" /> Reject
                            </button>
                            <button type="button" className="apa-link-btn" onClick={() => setCheckedIds([])}>
                                Clear
                            </button>
                        </div>
                    )}

                    <div className="apa-legend">
                        <span>
                            <i className="apa-legend-bar apa-legend-approved" /> <BadgeCheck size={13} aria-hidden="true" />{' '}
                            {isPm ? 'Approved by Purchase Manager - sent to Admin' : 'Approved by Purchase Manager'}
                        </span>
                        <span>
                            <i className="apa-legend-bar apa-legend-rejected" /> <XCircle size={13} aria-hidden="true" />{' '}
                            {isPm ? 'Rejected by Purchase Manager' : 'Rejected by Purchase Manager - you can still approve it'}
                        </span>
                    </div>

                    <div className="apa-table-wrap">
                        <table className="apa-table">
                            <thead>
                                <tr>
                                    <th className="apa-check-col">
                                        <input
                                            type="checkbox"
                                            aria-label="Select all on this page"
                                            checked={allPageChecked}
                                            disabled={pageIds.length === 0}
                                            onChange={toggleAllOnPage}
                                        />
                                    </th>
                                    <th>Sr. No.</th>
                                    <th>Requested By</th>
                                    <th>Date</th>
                                    <th>Item Name</th>
                                    <th>Category</th>
                                    <th>Quantity</th>
                                    <th className="apa-th-wrap">Estimated Cost (₹)</th>
                                    <th>Priority</th>
                                    {showStatusColumn && <th>Status</th>}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && items.length === 0 ? (
                                    <tr><td colSpan={columnCount} className="apa-empty">Loading...</td></tr>
                                ) : pageItems.length === 0 ? (
                                    <tr><td colSpan={columnCount} className="apa-empty">No product approvals found</td></tr>
                                ) : (
                                    pageItems.map((item, index) => {
                                        const pmDecision = pmDecisionOf(item);
                                        const pmReview = PM_REVIEW[pmDecision || 'none'];
                                        const cost = estimatedCostOf(item);
                                        const rowClass = [
                                            item._id === selectedId ? 'is-selected' : '',
                                            pmDecision === 'approved' ? 'is-pm-approved' : '',
                                            isRejectedByPm(item) ? 'is-pm-rejected' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ');
                                        return (
                                            <tr key={item._id} className={rowClass || undefined}>
                                                <td className="apa-check-col">
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Select ${item.productName}`}
                                                        checked={checkedIds.includes(item._id)}
                                                        onChange={() => toggleChecked(item._id)}
                                                    />
                                                </td>
                                                <td>{(safePage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                                <td className="apa-wrap">{requesterLabel(item)}</td>
                                                <td>{formatDate(item.createdAt)}</td>
                                                <td className="apa-wrap">
                                                    <button type="button" className="apa-item-link" onClick={() => setSelectedId(item._id)}>
                                                        {item.productName}
                                                    </button>
                                                    {pmReview.short && (
                                                        <span className={`apa-pm-icon apa-pm-icon-${pmReview.tone}`} title={pmReview.label}>
                                                            <pmReview.Icon size={14} aria-hidden="true" />
                                                            <span className="apa-sr-only">{pmReview.short}</span>
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{item.category}</td>
                                                <td>{item.quantity}</td>
                                                <td>{cost !== null ? formatRupees(cost) : '-'}</td>
                                                <td><PriorityBadge priority={item.priority} /></td>
                                                {showStatusColumn && <td className="apa-wrap">{statusText(item)}</td>}
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="apa-view-btn"
                                                        aria-label={`View ${item.productName}`}
                                                        onClick={() => setSelectedId(item._id)}
                                                    >
                                                        <Eye size={15} aria-hidden="true" /> View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {filtered.length > 0 && (
                        <div className="apa-pagination">
                            <span>
                                Showing {(safePage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                                {Math.min(safePage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}{' '}
                                {tab === 'pending' ? 'pending approvals' : 'product approvals'}
                            </span>
                            <div className="apa-pagination-controls">
                                <button
                                    type="button"
                                    aria-label="Previous page"
                                    disabled={safePage === 1}
                                    onClick={() => setPage(safePage - 1)}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                {pageButtons.map((num) => (
                                    <button
                                        type="button"
                                        key={num}
                                        className={num === safePage ? 'active' : ''}
                                        onClick={() => setPage(num)}
                                    >
                                        {num}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    aria-label="Next page"
                                    disabled={safePage === totalPages}
                                    onClick={() => setPage(safePage + 1)}
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {selected && (
                    <ApprovalDetails
                        key={selected._id}
                        approval={selected}
                        viewer={viewer}
                        busy={busy}
                        onClose={() => setSelectedId(null)}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onOpenForm={(approval) => openForm(approval)}
                    />
                )}
            </div>

            {formApproval && (
                <NewProductApprovalModal
                    mode={access.canApprove(formApproval) ? access.reviewMode : 'view'}
                    startAccepted={formAccepted}
                    approval={formApproval}
                    balagruhas={balagruhas}
                    onClose={() => setFormApproval(null)}
                    onDone={() => {
                        setFormApproval(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}
